import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { AlertCircle, CheckCircle2, UserPlus } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchSelect } from '@/components/search-select'
import { ServiceTierPicker } from '@/components/service-tier'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { asServiceTier, type ServiceTier } from '@/lib/service-tier'
import { cn } from '@/lib/utils'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { invalidateQuoteOverview, quoteQueryOptions } from '../api/queries'
import type { Address, Customer, NewCustomer, Quote } from '../api/types'
import { QuoteFiles } from '../files/quote-files'
import { useQuoteFileImport } from '../imports/use-quote-file-import'
import { blankAddress } from '../start/address'
import { AddressPanel, Field } from '../start/address-panel'
import { CustomerSearch } from '../start/customer-search'
import { AiData } from '../ui/ai-data'
import { formatQuoteDate } from '../ui/format'
import { QuoteStatusPill } from '../ui/status'
import { Money } from '../ui/values'
import type { WorkflowTabProps } from '../workflow/types'
import {
  addressCheckQueryOptions,
  customerHistoryQueryOptions,
  customerIdentityQueryOptions,
  type CustomerInfoUpdate,
  type QuoteContact,
} from './customer-info-api'

// Customer stage (USA-1023), ported from the demo's features/workflow/customer-info-tab.tsx.

type Dates = Pick<Quote, 'quote_due_date' | 'opening_date' | 'latest_delivery_date'>
type CustomerSnapshot = { customer_notes?: string; ai_insights?: string[] }

const dateFields = [
  ['quote_due_date', 'Due by'],
  ['opening_date', 'Opening date'],
  ['latest_delivery_date', 'Latest deliver by'],
] as const

// A stored address may lack fields (older quotes, imports); fill them so every field is a string.
const asAddress = (value: Quote['bill_to']): Address => ({ ...blankAddress(), ...(value as Partial<Address>) })
// A quote with no tier picked follows the dropship rule: Service with a deliver-by date (USA-455).
const quoteTier = (quote: Quote): ServiceTier =>
  quote.service_tier ? asServiceTier(quote.service_tier) : quote.latest_delivery_date ? 'service' : 'standard'
const snapshotOf = (quote: Quote) => (quote.customer_snapshot ?? {}) as CustomerSnapshot

function saveError(error: unknown) {
  if (error instanceof QuoteBuilderApiError) {
    if (error.status === 412) return 'This quote changed again while saving. Your edits are still here; try again.'
    if (error.code === 'customer_not_found') return 'That customer no longer exists. Choose another.'
    if (error.code === 'contact_not_found') return 'That contact no longer exists. Choose another.'
    if (error.code === 'project_already_started') return 'The project has started, so the customer details are locked.'
    if (error.status === 422) return 'Check the highlighted details and try again.'
  }
  return 'Could not save. Try again.'
}

/**
 * Saves run one at a time against the newest revision, like the demo's QuoteMutationClient: an
 * edit replaces a not-yet-sent edit with the same key, and a save that loses a race to another tab
 * re-reads the quote and applies its fields once more.
 */
function useCustomerInfoSave(quote: Quote, onQuoteChange: (quote: Quote) => void) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const chain = useRef<Promise<unknown>>(Promise.resolve())
  const waiting = useRef(new Map<string, { changes: CustomerInfoUpdate; result: Promise<Quote> }>())
  const revision = useRef(quote.revision)
  const [pending, setPending] = useState(0)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  revision.current = Math.max(revision.current, quote.revision)

  const send = async (changes: CustomerInfoUpdate) => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        const next = await api.request<Quote>(`/quotes/${quote.id}/customer-info`, {
          method: 'PATCH',
          body: JSON.stringify({ expected_revision: revision.current, ...changes }),
        })
        revision.current = Math.max(revision.current, next.revision)
        onQuoteChange(next)
        return next
      } catch (cause) {
        if (attempt === 0 && cause instanceof QuoteBuilderApiError && cause.status === 412) {
          const fresh = await client.fetchQuery({ ...quoteQueryOptions(api, quote.id), staleTime: 0 })
          revision.current = Math.max(revision.current, fresh.revision)
          continue
        }
        throw cause
      }
    }
  }

  const save = (changes: CustomerInfoUpdate, key = Object.keys(changes).sort().join(',')) => {
    setError('')
    const queued = waiting.current.get(key)
    if (queued) {
      queued.changes = changes
      return queued.result
    }
    setPending((count) => count + 1)
    const entry = { changes, result: Promise.resolve(quote) }
    entry.result = chain.current.then(() => {
      waiting.current.delete(key)
      return send(entry.changes)
    })
    waiting.current.set(key, entry)
    chain.current = entry.result.catch(() => undefined)
    entry.result
      .then(() => setSaved(true))
      .catch((cause) => setError(saveError(cause)))
      .finally(() => setPending((count) => count - 1))
    return entry.result
  }
  return { save, pending: pending > 0, saved, error }
}

/** Keeps a local edit unless the server value it started from changed (demo field rebase). */
function useRebased<T>(server: T, equal: (a: T, b: T) => boolean = Object.is) {
  const [value, setValue] = useState(server)
  const base = useRef(server)
  useEffect(() => {
    const previous = base.current
    base.current = server
    setValue((current) => (equal(current, previous) ? server : current))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server])
  return [value, setValue] as const
}

const sameAddress = (a: Address, b: Address) => JSON.stringify(a) === JSON.stringify(b)

function AddressStatus({ quoteId, address, onUse, disabled }: { quoteId: string; address: Address; onUse: (address: Address) => void; disabled?: boolean }) {
  const api = useQuoteBuilderApi()
  const check = useQuery(addressCheckQueryOptions(api, quoteId, address))
  if (!check.data) {
    return check.isFetching ? <p className="text-xs text-muted-foreground" role="status">Checking address…</p> : null
  }
  const { status, suggestion, message } = check.data
  if (status === 'confirmed') {
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CheckCircle2 aria-hidden="true" className="size-3.5 text-emerald-600" />
        Address confirmed
      </p>
    )
  }
  if (status === 'corrected' && suggestion) {
    const suggested = [suggestion.line1, suggestion.line2, suggestion.city, `${suggestion.state} ${suggestion.postal_code}`].filter((part) => part.trim()).join(', ')
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
        <span className="flex min-w-0 flex-1 items-start gap-1.5">
          <AlertCircle aria-hidden="true" className="mt-px size-3.5 shrink-0 text-amber-600" />
          <span className="min-w-0 break-words">
            Suggested: <span className="font-medium">{suggested}</span>
          </span>
        </span>
        <Button disabled={disabled} onClick={() => onUse({ ...address, line1: suggestion.line1, line2: suggestion.line2, city: suggestion.city, state: suggestion.state, postal_code: suggestion.postal_code })} size="sm" variant="outline">
          Use suggested
        </Button>
      </div>
    )
  }
  if (status === 'unconfirmed') {
    return (
      <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-500" role="status">
        <AlertCircle aria-hidden="true" className="mt-px size-3.5 shrink-0" />
        {message}
      </p>
    )
  }
  return null
}

function ContactPicker({
  contact,
  contacts,
  disabled,
  onChoose,
}: {
  contact: QuoteContact | null | undefined
  contacts: QuoteContact[]
  disabled?: boolean
  onChoose: (choice: CustomerInfoUpdate) => void
}) {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const valueOf = (item: QuoteContact) => (item.id ? `id:${item.id}` : `email:${item.email}`)
  const submit = (event: FormEvent) => {
    event.preventDefault()
    onChoose({ new_contact: { name: name.trim(), email: email.trim() } })
    setAdding(false)
    setName('')
    setEmail('')
  }
  return (
    <div className="grid gap-2">
      <Field label="Contact">
        {(id) => (
          <SearchSelect
            id={id}
            className="h-9 w-full min-w-0 bg-card"
            disabled={disabled}
            onChange={(value) => {
              if (value === 'add') return setAdding(true)
              const chosen = contacts.find((item) => valueOf(item) === value)
              if (!chosen) return
              onChoose(chosen.id ? { contact_id: chosen.id } : { new_contact: { name: chosen.name || chosen.email, email: chosen.email } })
            }}
            options={[
              ...contacts.map((item) => ({
                value: valueOf(item),
                label: item.name || item.email,
                description: item.name ? item.email : null,
              })),
              { value: 'add', label: 'Add a contact…' },
            ]}
            placeholder="Choose a contact"
            searchPlaceholder="Search contacts"
            value={contact ? valueOf(contact) : undefined}
          />
        )}
      </Field>
      {contact?.id ? (
        <Link className="break-words text-sm text-primary hover:underline" params={{ contactId: String(contact.id) }} to="/contacts/$contactId">
          {contact.email}
        </Link>
      ) : null}
      {adding ? (
        <form className="grid gap-3 rounded-lg border bg-muted/30 p-3" onSubmit={submit}>
          <Field label="Name">{(id) => <InlineFieldInput id={id} onChange={(event) => setName(event.target.value)} required value={name} />}</Field>
          <Field label="Email">{(id) => <InlineFieldInput id={id} onChange={(event) => setEmail(event.target.value)} required type="email" value={email} />}</Field>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setAdding(false)} size="sm" type="button" variant="ghost">
              Cancel
            </Button>
            <Button disabled={disabled} size="sm" type="submit">
              <UserPlus aria-hidden="true" />
              Add contact
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  )
}

function NewCustomerForm({ quote, onCreated }: { quote: Quote; onCreated: (customer: Customer) => void }) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const [values, setValues] = useState({ company_name: '', contact_name: '', email: '', phone: '' })
  const create = useMutation({
    mutationFn: (body: NewCustomer) => api.request<Customer>('/customers', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (customer) => {
      void client.invalidateQueries({ queryKey: ['customer-search'] })
      onCreated(customer)
    },
  })
  const set = (key: keyof typeof values) => (event: React.ChangeEvent<HTMLInputElement>) => setValues((current) => ({ ...current, [key]: event.target.value }))
  const submit = (event: FormEvent) => {
    event.preventDefault()
    // The new customer keeps this quote's addresses, so changing customers does not clear them.
    create.mutate({ ...values, phone: values.phone || null, billing_address: asAddress(quote.bill_to), shipping_address: asAddress(quote.deliver_to) })
  }
  return (
    <form className="grid gap-3" onSubmit={submit}>
      <Field label="Company">{(id) => <InlineFieldInput id={id} onChange={set('company_name')} required value={values.company_name} />}</Field>
      <Field label="Contact">{(id) => <InlineFieldInput id={id} onChange={set('contact_name')} required value={values.contact_name} />}</Field>
      <Field label="Email">{(id) => <InlineFieldInput id={id} onChange={set('email')} required type="email" value={values.email} />}</Field>
      <Field label="Phone">{(id) => <InlineFieldInput id={id} onChange={set('phone')} type="tel" value={values.phone} />}</Field>
      {create.error ? (
        <p className="text-sm text-destructive" role="alert">
          Could not add the customer. Check the details and try again.
        </p>
      ) : null}
      <div className="flex justify-end">
        <LoadingButton loading={create.isPending} loadingText="Adding…" size="sm" type="submit">
          Add customer
        </LoadingButton>
      </div>
    </form>
  )
}

export function CustomerHistory({ quoteId, customerId, active = true }: { quoteId: string; customerId: number; active?: boolean }) {
  const api = useQuoteBuilderApi()
  const [filter, setFilter] = useState<'all' | 'orders' | 'accepted' | 'quotations'>('all')
  const history = useQuery({ ...customerHistoryQueryOptions(api, customerId), enabled: active })
  const rows = (history.data ?? []).filter((item) => item.id !== quoteId)
  const kind = (item: Quote) => (item.status === 'started' ? 'orders' : item.status === 'approved' ? 'accepted' : 'quotations')
  const visible = rows.filter((item) => filter === 'all' || kind(item) === filter)
  const filters = [
    ['all', 'All'],
    ['orders', 'Orders'],
    ['accepted', 'Accepted quotes'],
    ['quotations', 'Quotations'],
  ] as const
  return (
    <section aria-label="Customer history" className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <h2 className="text-base font-semibold">Customer history</h2>
        <div aria-label="History type" className="flex flex-wrap gap-1" role="group">
          {filters.map(([value, label]) => (
            <button
              aria-pressed={filter === value}
              className={cn(
                'rounded-md px-2.5 py-1.5 text-xs font-medium outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
                filter === value ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60',
              )}
              key={value}
              onClick={() => setFilter(value)}
              type="button"
            >
              {label} <span className="ml-1 tabular-nums">{rows.filter((row) => value === 'all' || kind(row) === value).length}</span>
            </button>
          ))}
        </div>
      </div>
      {history.isLoading ? (
        <p className="p-4 text-sm text-muted-foreground">Loading customer history…</p>
      ) : history.isError ? (
        <div className="flex items-center gap-2 p-4 text-sm text-destructive" role="alert">
          History could not load.
          <Button onClick={() => void history.refetch()} size="sm" variant="outline">
            Try again
          </Button>
        </div>
      ) : visible.length ? (
        <ul className="divide-y">
          {visible.map((item) => {
            const items = item.lines.filter((line) => !line.parent_line_id)
            return (
              <li className="grid gap-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto]" key={item.id}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <Link className="font-semibold text-primary hover:underline" params={{ quoteId: item.id, stage: 'customer' }} to="/quotes/$quoteId/workflow/$stage">
                      {item.quote_number}
                    </Link>
                    <span className="text-xs text-muted-foreground">{formatQuoteDate(item.quote_date)}</span>
                  </div>
                  <p className="mt-1 break-words text-sm">{item.title}</p>
                  {items.length ? (
                    <ul className="mt-1.5 grid gap-0.5 text-xs text-muted-foreground">
                      {items.slice(0, 3).map((line) => (
                        <li className="break-words" key={line.id}>
                          <span className="tabular-nums">{Number(line.quantity).toLocaleString('en-US')} × </span>
                          {[line.manufacturer, line.model || line.description].filter(Boolean).join(' ')}
                        </li>
                      ))}
                      {items.length > 3 ? <li>{items.length - 3} more items</li> : null}
                    </ul>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground">No equipment added</p>
                  )}
                </div>
                <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
                  <Money value={item.totals.grand_total} />
                  <QuoteStatusPill status={item.status} />
                </div>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="p-4 text-sm text-muted-foreground">{rows.length ? 'No matching history.' : 'No other quotes for this customer.'}</p>
      )}
    </section>
  )
}

export function CustomerInfoTab({ active = true, quoteId, quote, workflow, onQuoteChange }: WorkflowTabProps) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const { save, pending, saved, error } = useCustomerInfoSave(quote, onQuoteChange)
  const identity = useQuery({ ...customerIdentityQueryOptions(api, quote), enabled: active && quote.customer_id != null })
  const [changing, setChanging] = useState<false | 'existing' | 'new'>(false)
  const [title, setTitle] = useRebased(quote.title)
  const serverDates: Dates = { quote_due_date: quote.quote_due_date ?? null, opening_date: quote.opening_date ?? null, latest_delivery_date: quote.latest_delivery_date ?? null }
  const [dates, setDates] = useRebased(serverDates, (a, b) => JSON.stringify(a) === JSON.stringify(b))
  const [tier, setTier] = useRebased(quoteTier(quote))
  const [billTo, setBillTo] = useRebased(asAddress(quote.bill_to), sameAddress)
  const [deliverTo, setDeliverTo] = useRebased(asAddress(quote.deliver_to), sameAddress)
  const snapshot = snapshotOf(quote)
  const [notes, setNotes] = useRebased(snapshot.customer_notes ?? '')
  const busy = workflow.state === 'started'
  const openImport = useQuoteFileImport(quoteId)
  // A missing bill-to or ship-to blocker links here with ?focus=bill_to|deliver_to (USA-1140).
  const search = useSearch({ strict: false }) as { focus?: unknown }
  const focus = search.focus === 'bill_to' || search.focus === 'deliver_to' ? search.focus : null
  useEffect(() => {
    if (!active || !focus) return
    const panel = document.getElementById(`quote-${focus}`)
    if (!panel) return
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
    const empty = Array.from(panel.querySelectorAll<HTMLInputElement>('input')).find(
      // Address 1, City, State and ZIP: what a bill-to or ship-to needs.
      (input, index) => [4, 6, 7, 8].includes(index) && !input.value.trim(),
    )
    empty?.focus({ preventScroll: true })
  }, [active, focus])

  const commitAddress = (field: 'bill_to' | 'deliver_to', value: Address) => {
    if (!sameAddress(value, asAddress(quote[field]))) void save({ [field]: value })
  }
  const chooseCustomer = (customerId: number) => {
    setChanging(false)
    if (customerId === quote.customer_id) return
    void save({ customer_id: customerId }).then(() => {
      void invalidateQuoteOverview(client)
    })
  }
  const contact = identity.data?.contact

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <section aria-label="Customer" className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-4 rounded-xl border bg-card p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold">Customer</h2>
            <Button
              disabled={busy}
              onClick={() => setChanging(changing ? false : 'existing')}
              size="sm"
              variant={quote.customer || changing ? 'ghost' : 'default'}
            >
              {changing ? 'Cancel' : quote.customer ? 'Change customer' : 'Add customer'}
            </Button>
          </div>
          {changing ? (
            <div className="grid gap-3">
              <Tabs onValueChange={(value) => setChanging(value as 'existing' | 'new')} value={changing}>
                <TabsList aria-label="Customer source" className="w-full">
                  <TabsTrigger className="flex-1" value="existing">
                    Existing
                  </TabsTrigger>
                  <TabsTrigger className="flex-1" value="new">
                    New customer
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              {changing === 'existing' ? (
                <CustomerSearch active={active} disabled={pending} onSelect={(customer) => chooseCustomer(customer.id)} selectedId={quote.customer_id ?? undefined} />
              ) : (
                <NewCustomerForm onCreated={(customer) => chooseCustomer(customer.id)} quote={quote} />
              )}
            </div>
          ) : null}
          {!quote.customer ? (
            <p className="text-sm text-muted-foreground">No customer yet</p>
          ) : null}
          <div className={cn('grid gap-1', !quote.customer && 'hidden')}>
            <span className="text-xs font-medium text-muted-foreground">Company</span>
            {identity.data ? (
              <Link className="break-words text-sm font-semibold text-primary hover:underline" params={{ groupId: identity.data.uuid }} to="/customers/$groupId">
                {identity.data.company_name}
              </Link>
            ) : (
              <span className="text-sm font-semibold">{quote.customer?.company_name}</span>
            )}
            {identity.data?.phone ? <span className="text-sm text-muted-foreground">{identity.data.phone}</span> : null}
          </div>
          {!quote.customer ? null : identity.isError ? (
            <div className="flex items-center gap-2 text-sm text-destructive" role="alert">
              Contacts could not load.
              <Button onClick={() => void identity.refetch()} size="sm" variant="outline">
                Retry
              </Button>
            </div>
          ) : (
            <ContactPicker contact={contact} contacts={identity.data?.contacts ?? []} disabled={busy || !identity.data} onChoose={(choice) => void save(choice)} />
          )}
          <div className="grid gap-1">
            <span className="text-xs font-medium text-muted-foreground">Project name</span>
            <POInlineEditableField
              ariaLabel="Project name"
              buttonClassName="-mx-2 w-[calc(100%+1rem)] justify-between"
              disabled={busy}
              onConfirm={(raw) => {
                // The old always-open field capped typing at 255 characters.
                const next = raw.slice(0, 255)
                setTitle(next)
                if (next.trim() && next !== quote.title) void save({ title: next.trim() })
              }}
              placeholder="Add project name"
              showEditIcon
              title="Edit project name"
              value={title}
            />
          </div>
        </section>

        <section aria-label="Project" className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-3">
          <section className="rounded-xl border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold">Project dates</h2>
              <span className="text-xs text-muted-foreground" role="status">
                {error ? '' : pending ? 'Saving…' : saved ? 'Saved' : ''}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-4">
              {dateFields.map(([field, label]) => (
                <Field key={field} label={label}>
                  {(id) => (
                    <Input
                      className="min-w-0 bg-card"
                      disabled={busy}
                      id={id}
                      onBlur={() => {
                        if ((dates[field] ?? null) !== (quote[field] ?? null)) void save({ [field]: dates[field] || null })
                      }}
                      onChange={(event) => setDates((current) => ({ ...current, [field]: event.target.value || null }))}
                      type="date"
                      value={dates[field] ?? ''}
                    />
                  )}
                </Field>
              ))}
              <div className="grid content-start gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Service tier</span>
                <div className="flex h-9 items-center">
                  <ServiceTierPicker
                    disabled={busy}
                    pending={pending && tier !== quoteTier(quote)}
                    tier={tier}
                    onSelect={(next) => {
                      setTier(next)
                      void save({ service_tier: next })
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
          <AddressPanel
            actionLabel="Use customer"
            disabled={busy}
            onAction={() => {
              const value = { ...billTo, name: identity.data?.company_name ?? billTo.name, contact_name: contact?.name ?? billTo.contact_name, email: contact?.email ?? billTo.email, phone: identity.data?.phone ?? billTo.phone }
              setBillTo(value)
              commitAddress('bill_to', value)
            }}
            onChange={setBillTo}
            onCommit={(value) => commitAddress('bill_to', value)}
            id="quote-bill_to"
            open={focus === 'bill_to'}
            status={<AddressStatus address={asAddress(quote.bill_to)} disabled={busy} onUse={(value) => { setBillTo(value); commitAddress('bill_to', value) }} quoteId={quoteId} />}
            title="Bill To"
            value={billTo}
          />
          <AddressPanel
            actionLabel="Use Bill To"
            disabled={busy}
            onAction={() => {
              setDeliverTo({ ...billTo })
              commitAddress('deliver_to', { ...billTo })
            }}
            onChange={setDeliverTo}
            onCommit={(value) => commitAddress('deliver_to', value)}
            id="quote-deliver_to"
            open={focus === 'deliver_to'}
            status={<AddressStatus address={asAddress(quote.deliver_to)} disabled={busy} onUse={(value) => { setDeliverTo(value); commitAddress('deliver_to', value) }} quoteId={quoteId} />}
            title="Deliver To"
            value={deliverTo}
          />
          <QuoteFiles disabled={busy} onImport={openImport} quoteId={quoteId} />
        </section>
      </div>

      <section className="grid gap-4 rounded-xl border bg-card p-4 lg:grid-cols-2">
        <div className="grid content-start gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Customer notes · Internal</span>
          <POInlineEditableField
            allowWrap
            ariaLabel="Customer notes · Internal"
            buttonClassName="-mx-2 w-[calc(100%+1rem)] items-start"
            disabled={busy}
            inputClassName="min-h-24 resize-y"
            kind="textarea"
            onConfirm={(raw) => {
              // The old always-open box capped typing at 10,000 characters.
              const next = raw.slice(0, 10000)
              setNotes(next)
              if (next !== (snapshot.customer_notes ?? '')) void save({ customer_notes: next })
            }}
            placeholder="Deadlines, delivery needs and customer preferences"
            showEditIcon
            title="Edit customer notes"
            value={notes}
          />
        </div>
        {snapshot.ai_insights?.length ? (
          <div className="grid content-start gap-2">
            <h2 className="text-sm font-semibold">AI customer insights</h2>
            <AiData className="px-3 py-2" label="AI customer insights">
              <ul className="grid gap-2 text-sm leading-5">
                {snapshot.ai_insights.map((insight, index) => (
                  <li key={index}>{insight}</li>
                ))}
              </ul>
            </AiData>
          </div>
        ) : null}
      </section>
      {quote.customer_id != null ? (
        <CustomerHistory active={active} customerId={quote.customer_id} key={quote.customer_id} quoteId={quoteId} />
      ) : null}
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
